import React, { useEffect, useState } from 'react';
import store, { searchActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { Accordion, HelpText } from '../common/SharedComponents';
import debounce from 'lodash.debounce';
import { TabPanel, Item } from 'devextreme-react/tab-panel';
import { BfiCustomStore } from '../common/SharedComponents';
import NonBfiTable from './tables/NonBfiTable';
import BfiOutstandingTable from './tables/BfiOutstandingTable';
import BfiRepaidTable from './tables/BfiRepaidTable';
import NonBfiDetailModal from './modals/NonBfiDetailModal';
import BfiDetailModal from './modals/BfiDetailModal';
import AuditHistoryModal from './modals/AuditHistoryModal';
import { BorrowerApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { executeNonBFIExperienceAction } from './common/ExperienceFunctions';
import { NonBFIExperienceActionType } from './common/ExperienceConstants';
import { ExperienceLevelSection } from './ExperienceLevelSection';
import { PropertyMap } from './common/ExperienceComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { PermissionType } from '@apply/constants';
import { hasPermission } from '@apply/ui';
import { LoanModals } from '../loans/common/LoanComponents';

function ExperienceLandingPage() {
    const searchState = useSelector((state) => state.searchState);
    const { dispatch } = store;

    const styles = {
        principalSummary: {
            padding: '10px',
            backgroundColor: 'white',
            marginBottom: '10px',
        },
        principalName: {
            fontSize: 'larger',
            color: '#2c5591',
            fontWeight: 'bold',
        },
        tables: { padding: '0 10px' },
        mainSearch: { borderBottom: 'none', marginTop: '5px' },
        content: { padding: '0.5rem' },
    };

    const borrowerApi = new BorrowerApi.BorrowerApi(applicationInsights);
    const bfiRepaidStore = new BfiCustomStore();
    const bfiOutstandingStore = new BfiCustomStore();

    const url = window.location.pathname;
    const principalId = parseInt(url.split('/')[3]);
    const debouncedSearch = debounce(dispatch, 500);

    const [principal, set_principal] = useState(null);

    const _onSearchChange = (event) => {
        debouncedSearch(
            searchActions.setSearchText({
                searchText: {
                    ...searchState.searchText,
                    experience: event.target.value,
                },
            })
        );
    };

    useEffect(() => {
        return () => {
            //Cancel any further searches if component is unloaded
            debouncedSearch.cancel();
        };
    }, []);

    useEffect(() => {
        if (!principalId) {
            return;
        }

        const getPrincipalAsync = async (id) => {
            const response = await borrowerApi.getPrincipalSummaryById(id);
            if (!response || response.statusCode !== 200) {
                return;
            }
            set_principal(response.result);
        };

        getPrincipalAsync(principalId);
    }, [principalId]);

    const onAddExperience = () => {
        executeNonBFIExperienceAction({
            id: NonBFIExperienceActionType.AddNonBfi,
            experienceId: 0,
            principalId,
        });
    };

    const canEdit = hasPermission(PermissionType.ExperienceEditor);

    if (!principalId || !principal) {
        return null;
    }

    return (
        <div>
            <div
                className='page-header'
                role='banner'
            >
                <h1>Principal Experience</h1>
                <div className='primary-actions'>
                    <button
                        className='hollow'
                        onClick={() => onAddExperience()}
                        disabled={!canEdit || !principal.hasTwentyPercentEntities}
                        style={{ padding: '10px 20px' }}
                        title={
                            principal.hasTwentyPercentEntities
                                ? 'Add Non-BFI Experience'
                                : 'No 20% + Entities available for this Principal'
                        }
                    >
                        <FontAwesomeIcon icon={faPlus} /> Add Non-BFI Experience
                    </button>
                    &nbsp;
                    <HelpText />
                </div>
            </div>
            <div
                className='main-content no-search'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div
                    className='scrollable-content-without-search'
                    style={styles.content}
                >
                    <div style={styles.principalSummary}>
                        Principal: <span style={styles.principalName}>{principal && principal.name}</span>
                    </div>
                    <div
                        className='experience accordion'
                        data-accordion=''
                        data-allow-all-closed='true'
                        data-multi-expand='true'
                    >
                        <TabPanel tabsPosition={'left'}>
                            <Item title={'Experience'}>
                                <div
                                    className='input-has-icon main-search'
                                    style={styles.mainSearch}
                                >
                                    <div>
                                        <input
                                            type='search'
                                            placeholder='Search on BFI #, address, city, state or zip...'
                                            defaultValue={searchState?.searchText?.experience}
                                            onChange={(event) => _onSearchChange(event)}
                                        />
                                        <FontAwesomeIcon
                                            icon={faSearch}
                                            aria-hidden='true'
                                            className='icon'
                                        />
                                    </div>
                                </div>
                                <div style={styles.tables}>
                                    <Accordion
                                        title={'Non-BFI Experience'}
                                        isExpanded={true}
                                        id='acc_nonbfi'
                                    >
                                        <div className='card'>
                                            <div style={{ padding: '10px' }}>
                                                <NonBfiTable principalId={principalId} />
                                            </div>
                                        </div>
                                    </Accordion>
                                    <Accordion
                                        title={'BFI - Repaid'}
                                        isExpanded={true}
                                        id='acc_bfiRepaid'
                                    >
                                        <div className='card'>
                                            <div style={{ padding: '10px' }}>
                                                <BfiRepaidTable
                                                    principalId={principalId}
                                                    gridStore={bfiRepaidStore}
                                                />
                                            </div>
                                        </div>
                                    </Accordion>
                                    <Accordion
                                        title={'BFI - Outstanding'}
                                        isExpanded={true}
                                        id='acc_bfiOutstanding'
                                    >
                                        <div className='card'>
                                            <div style={{ padding: '10px' }}>
                                                <BfiOutstandingTable
                                                    principalId={principalId}
                                                    gridStore={bfiOutstandingStore}
                                                />
                                            </div>
                                        </div>
                                    </Accordion>
                                </div>
                            </Item>
                            <Item title={'Property Map'}>
                                <div
                                    style={{
                                        padding: '10px',
                                        height: '900px',
                                        width: '100%',
                                    }}
                                >
                                    <PropertyMap principalId={principalId} />
                                </div>
                            </Item>
                            <Item title={'Level'}>
                                <ExperienceLevelSection
                                    searchState={searchState}
                                    principalId={principalId}
                                />
                            </Item>
                        </TabPanel>
                    </div>
                </div>
            </div>
            <NonBfiDetailModal />
            <BfiDetailModal />
            <AuditHistoryModal />
            <LoanModals />
        </div>
    );
}

export default ExperienceLandingPage;
